<template>
  <div class="share-resume-popup" @click="toggleHelp">
    <div class="header">
      <div class="title">
        {{ $t('popUp.shareResume.title.main') }} &nbsp;
        <span class="ellipsis"> {{ resume.candidate.firstname }}</span> &nbsp;{{ $t('popUp.shareResume.title.with') }}
      </div>
    </div>

    <div class="share-resume-container">
      <div class="section">
        <div class="container">
          <div class="title-container no-margin">
            <div class="title">
              <span> {{ $t('popUp.shareResume.sendInvitation') }}</span>
              <div ref="helpIcon" class="help-icon-container">
                <HelpIcon class="help-icon" />
                <div class="info" :class="{ active: helpIsOpened }">
                  Veuillez renseigner le mail avec lequel vous souhaitez partager ce dossier
                </div>
              </div>
            </div>
          </div>
          <div class="field" :class="{ 'no-margin': !contributors.length }">
            <input ref="email" v-model="email" placeholder="adresse@mail.com" type="text" class="text-input opacity" />
          </div>
        </div>
        <div v-if="contributors.length" class="container">
          <div class="title-container no-margin">
            <div class="title">{{ $t('popUp.shareResume.sharedWith') }}</div>
            <div class="indicator">
              {{ contributors.length }}
              {{
                contributors.length > 1
                  ? $t('popUp.shareResume.person.plural')
                  : $t('popUp.shareResume.person.singular')
              }}
            </div>
          </div>
          <div class="row users">
            <div v-for="contributor in contributors" :key="contributor.id" class="user">
              <div class="line">
                <div class="name">
                  {{
                    contributor.firstName && contributor.lastName
                      ? contributor.firstName + ' ' + contributor.lastName
                      : contributor.email
                  }}
                </div>
                <div v-if="contributor.id === user.id" class="member-since">
                  {{
                    $t(`dashboard.invitationStatus._${0}`, {
                      since: formatDate(contributor.id === user.id ? resume.createdAt : contributor.since),
                    })
                  }}
                </div>
                <div v-else class="member-since">
                  {{
                    contributor.lastVisitAt !== null
                      ? `${$t('popUp.shareResume.lastVisit')} ${formatDate(contributor.lastVisitAt)}`
                      : $t('popUp.shareResume.invitationSend')
                  }}
                </div>
              </div>
              <div class="line">
                <div class="email">
                  {{ contributor.firstName && contributor.lastName ? contributor.email : '' }}
                </div>
                <div class="revoque" @click.stop="kickUser(contributor)">
                  <crossArrowIcon class="icon" />
                  {{
                    contributor.id === user.id
                      ? $t('popUp.shareResume.leaveOrganization')
                      : $t('popUp.shareResume.kick')
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="container center">
          <div class="main-button button-cancel" @click="popupResolve(false)">
            <div class="text">{{ $t('popUp.shareResume.cancelButton') }}</div>
          </div>
          <div class="main-button button-create" @click="share">
            <div class="text">{{ $t('popUp.shareResume.validateButton') }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatDate } from '@/libraries/formatter';
import crossArrowIcon from '@/assets/icons/cross_arrow.svg';
import HelpIcon from '@/assets/icons/help.svg';

export default {
  name: 'ShareResume',
  components: {
    crossArrowIcon,
    HelpIcon,
  },
  data: function () {
    return {
      resume: null,
      isOrganization: true,
      helpIsOpened: false,
      email: '',
      identifier: '',
      jobTitle: '',
      referenceLink: '',
      templateIdSelected: null,
      contactId: null,
      contributors: [],
      formatDate,
    };
  },
  computed: {
    ...mapGetters(['popupResolve', 'popupReject', 'popupData', 'user']),
  },
  watch: {
    email() {
      this.validEmail(this.email);
    },
  },
  async created() {
    this.resume = this.popupData;
    const result = await this.$store.dispatch('resumes.share.getById', {
      resumeId: this.resume.id,
    });
    this.contributors = result.collabos;
  },
  mounted() {
    this.$refs.email.focus();
  },
  methods: {
    validEmail: function (email) {
      var re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      const result = re.test(email);
      if (result) {
        this.$refs.email.setCustomValidity('');
      }
      return result;
    },
    async share() {
      if (this.validEmail(this.email)) {
        try {
          await this.$store.dispatch('resumes.share', {
            resumeId: this.resume.id,
            email: this.email,
            permission: 2,
          });
          this.popupResolve(true);
        } catch (e) {
          console.error(e);
          this.popupResolve(e.response.data);
        }
      } else {
        this.$refs.email.setCustomValidity('Invalid');
      }
    },
    async kickUser(user) {
      try {
        if (confirm('Are you sure')) {
          await this.$store.dispatch('resume.unshare', {
            resumeId: this.resume.id,
            userId: user.id,
          });
          this.popupResolve(true);
        }
      } catch (e) {
        console.error(e);
      }
    },
    toggleHelp(e) {
      if (this.$refs.helpIcon) {
        if (!this.$refs.helpIcon.contains(e.target) || this.helpIsOpened) {
          this.helpIsOpened = false;
        } else {
          this.helpIsOpened = true;
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/inputs.scss';
@use '@/styles/buttons.scss';
@use '@/styles/responsive/popup.scss';

.share-resume-popup {
  width: 646px;
  box-sizing: border-box;
  .header {
    border-bottom: 1px solid colors.$light-blue-color;
    padding-bottom: 13px;
    width: 100%;
    .title {
      display: flex;
      flex-wrap: wrap;
      color: colors.$light-blue-color;
      font-size: 16px;
      font-weight: bold;
      text-transform: uppercase;
      span {
        max-width: 54%;
      }
    }
  }
  .share-resume-container {
    display: flex;
    .section {
      padding: 18px 0 0;
      display: flex;
      flex-direction: column;
      position: relative;
      width: 100%;
      .container {
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        flex-direction: column;
        margin: 4px 0;
        &.center {
          width: 100%;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          padding: 0 30px;
          box-sizing: border-box;
          margin: 0 0 6px 0;

          .button-cancel {
            margin-right: 22px;
          }
        }
        .users {
          display: flex;
          align-items: flex-start;
          justify-content: flex-start;
          width: 100%;
          flex-direction: column;
        }
        .user {
          border: 1px solid #d0d0d0;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-direction: column;
          width: 100%;
          border-top: 1px solid #d0d0d0;
          padding: 14px;

          &:nth-child(2n) {
            border-top: 0;
            border-bottom: 0;
          }
          &:first-child {
            border-top-left-radius: 5px;
            border-top-right-radius: 5px;
          }
          &:last-child {
            border-bottom-left-radius: 5px;
            border-bottom-right-radius: 5px;
            border-bottom: 1px solid #d0d0d0;
          }

          .line {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;

            .name {
              color: #000000;
              font-size: 16px;
              display: flex;
              align-items: center;
              margin-right: 10px;
            }

            .email {
              height: 19px;
              width: 165px;
              color: #939292;
              font-family: Lato, sans-serif;
              font-size: 16px;
              line-height: 19px;
            }

            .member-since {
              color: #939292;
              font-size: 16px;
            }

            .revoque {
              color: #2d6470;
              font-size: 16px;
              font-weight: bold;
              cursor: pointer;
              transition: all 0.3s;
              display: flex;
              align-items: center;
              border-radius: 3px;

              .icon {
                width: 30px;
                height: 30px;
                margin-right: 5px;
              }

              &:hover {
                border-radius: 3px;
                background-color: rgba(#2d6470, 0.15);
                padding-right: 5px;
              }
            }
          }
        }
        .main-button {
          margin: 44px 0 0;
        }
      }
      .field {
        width: 100%;
        margin: 6px 0 30px 0;

        &.no-margin {
          margin: 6px 0 0 0;
        }

        .title {
          height: 19px;
          color: #939292;
          font-size: 16px;
          margin: 0 0 4px;
        }

        &.checkboxs {
          display: flex;
          align-items: center;
          .checkbox-container {
            display: flex;
            align-items: center;
            margin-right: 24px;
            cursor: pointer;
            .title {
              height: 19px;
              color: #333333;
              font-size: 16px;
              margin-left: 8px;
            }
          }
        }

        &.infos {
          height: 15px;
          width: 100%;
          text-align: right;
          color: colors.$light-blue-color;
          font-size: 12px;
          font-style: italic;
          margin: 0 0 30px;
        }
      }
      .section {
        &:last-child {
          border-bottom: none;
        }
      }
      .templates {
        display: flex;
        .template {
          width: 190px;
          height: 270px;
          border-radius: 4px;
          margin-right: 6px;
          background-position: center;
          background-size: contain;
          background-repeat: no-repeat;
          box-shadow:
            0 2px 8px -2px rgba(0, 0, 0, 0.1),
            0 4px 12px -4px rgba(0, 0, 0, 0.2);
          border: solid 1px transparent;
          cursor: pointer;
          position: relative;
          &.selected {
            border-color: colors.$light-blue-color;
          }
          .icon {
            width: 10px;
            height: 10px;
            color: #fff;
            position: absolute;
            top: 0;
            right: 0;
            background: colors.$light-blue-color;
            padding: 4px;
            border-radius: 4px;
            border-top-right-radius: 0;
            border-top-left-radius: 0;
            border-bottom-right-radius: 0;
          }
        }
      }
      .title-container {
        display: flex;
        align-items: center;
        justify-content: space-between;

        &.no-margin {
          margin: 0 0 12px 0;
          width: 100%;
        }
        .arrow {
          position: absolute;
          left: 0;
        }

        .title {
          color: #000000;
          font-size: 18px;
          font-weight: bold;
          display: flex;
          width: 100%;
          justify-content: space-between;
          align-items: center;

          .help-icon-container {
            position: relative;
            width: 20px;
            height: 20px;
            cursor: pointer;

            .help-icon {
              height: 100%;
              width: 100%;
              color: #333333;
              transition: all 0.2s;

              &:hover {
                opacity: 0.8;
              }
            }

            .info {
              position: absolute;
              background-color: #fff;
              box-shadow: 0px 1px 4px 0px rgba(75, 75, 75, 0.5);
              border: 2px solid rgba(45, 100, 112, 1);
              border-radius: 8px;
              padding: 9px;
              right: -14px;
              top: 150%;
              font-size: 0.9rem;
              text-align: center;
              line-height: 0.8rem;
              width: 10.5rem;
              transform-origin: 160px top;
              scale: 0;
              pointer-events: none;
              transition: all 0.2s ease-in-out;
              user-select: none;

              &.active {
                scale: 1;
                pointer-events: auto;
              }

              &::after {
                content: '';
                position: absolute;
                background-color: white;
                rotate: 45deg;
                top: -7.5px;
                right: 16px;
                width: 10px;
                height: 10px;
                border-left: 2px solid rgba(45, 100, 112, 1);
                border-top: 2px solid rgba(45, 100, 112, 1);
              }
            }
          }
        }
        .indicator {
          margin-left: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 29px;
          width: 140px;
          border-radius: 3px;
          background-color: #e4e4e4;
          color: #606060;
          font-size: 16px;
        }
      }
      .validate {
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }
}
</style>
